import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
} from 'react'
import { getTenant } from '../utils/tenant';
import { CurrentUserDto } from '../types/django/user' 
import { authService } from '../services/authService'

interface AuthContextType {
  user: CurrentUserDto | null 
  isLoading: boolean
  isAuthenticated: boolean
  tenant: string
  permissions: Record<string, boolean>
  login: (email: string, password: string) => Promise<void>
  logout: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<CurrentUserDto | null>(null) 
  const [isLoading, setIsLoading] = useState(true)
  const queryClient = useQueryClient();

  const tenant = useMemo(() => getTenant(), []);
  const isAuthenticated = !!user

  // Aligné sur la nouvelle structure de ton DTO/Backend (user.permissions)
  const permissions = useMemo(() => {
    if (!user) return {};
    return user.permissions || (user as any).role_group?.permissions || {};
  }, [user]);

  useEffect(() => {
    const init = async () => {
      try {
        const cachedUser = authService.getStoredUser()
        if (cachedUser) setUser(cachedUser)

        const token = authService.getAccessToken()
        // Si pas de token, on passe direct au bloc 'finally' pour couper le chargement
        if (!token) return; 

        const userData = await authService.fetchUser()
        setUser(userData)

      } catch {
        try {
          const newAccess = await authService.refreshAccessToken()

          if (newAccess) {
            const userData = await authService.fetchUser()
            setUser(userData)
          } else {
            authService.clearAuth()
            setUser(null)
          }
        } catch {
          authService.clearAuth()
          setUser(null)
        }
      } finally {
        setIsLoading(false) // S'exécute maintenant correctement dans tous les cas !
      }
    }

    init()
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    //Nettoie le cache avant de connecter le nouvel utilisateur
    queryClient.clear(); 
    
    await authService.login({ email, password })

    const userData = await authService.fetchUser()
    setUser(userData)
  }, [queryClient])

  const logout = useCallback(() => {
    authService.logout()
    queryClient.clear(); 
    setUser(null)
  }, [queryClient])

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated,
        tenant,
        permissions, 
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider')
  }

  return context
}